"use client";

/**
 * generation-started-modal.tsx
 *
 * Modal shown when a user triggers report generation. Flow:
 * 1. Check if a report already exists for this stock/fund
 * 2. If exists → "View existing" or "Generate fresh"
 * 3. If not → "Watch it generate" or "I'll read it later"
 * 4. If a generation is already in progress → blocking message
 */

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useGeneration } from "@/lib/generation-context";

export interface GenerationStartedModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportName: string;
  reportType: "stock" | "fund";
  generatePageUrl: string;
  /** For stocks: ticker */
  ticker?: string;
  /** For stocks: exchange */
  exchange?: string;
  /** For funds: schemeCode */
  schemeCode?: string;
}

type ExistingReport = {
  slug: string;
  name: string;
  score: number;
  ageDays: number;
  isOutdated: boolean;
};

export function GenerationStartedModal({
  isOpen,
  onClose,
  reportName,
  reportType,
  generatePageUrl,
  ticker,
  exchange,
  schemeCode,
}: GenerationStartedModalProps) {
  const router = useRouter();
  const { isGenerating, startBackgroundGeneration, generation, expand } =
    useGeneration();

  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState<ExistingReport | null>(null);
  const [checked, setChecked] = useState(false);

  // Check for existing report when modal opens
  useEffect(() => {
    if (!isOpen || isGenerating) return;
    // Reset state when modal opens
    setExisting(null);
    setChecked(false);
    setLoading(true);

    const controller = new AbortController();

    (async () => {
      try {
        let url = "";
        if (reportType === "stock" && ticker && exchange) {
          url = `/api/reports/check?ticker=${encodeURIComponent(ticker)}&exchange=${encodeURIComponent(exchange)}`;
        } else if (reportType === "fund" && schemeCode) {
          url = `/api/funds/check?schemeCode=${encodeURIComponent(schemeCode)}`;
        }

        if (!url) {
          setChecked(true);
          setLoading(false);
          return;
        }

        const res = await fetch(url, { signal: controller.signal });
        const json = await res.json();

        if (json.exists) {
          const r = json.report;
          setExisting({
            slug: r.slug,
            name: reportType === "fund" ? r.schemeName : r.companyName,
            score: r.score ?? 0,
            ageDays: r.ageDays ?? 0,
            isOutdated: r.isOutdated ?? false,
          });
        }
      } catch {
        // If check fails, proceed to generation flow
      } finally {
        if (!controller.signal.aborted) {
          setChecked(true);
          setLoading(false);
        }
      }
    })();

    return () => controller.abort();
  }, [isOpen, isGenerating, reportType, ticker, exchange, schemeCode]);

  if (!isOpen) return null;

  // ─── Already generating — block ──────────────────────────────────
  if (isGenerating) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-surface border border-surface-light/30 rounded-2xl p-8 max-w-[460px] w-full mx-4 shadow-2xl">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gold/20 flex items-center justify-center">
              <span className="text-2xl">&#9888;&#65039;</span>
            </div>
            <h2 className="text-foreground text-lg font-bold mb-2">
              Generation in progress
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed">
              A report for &ldquo;{generation?.reportName}&rdquo; is already
              being generated. Please wait for it to finish before starting
              another.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                onClose();
                expand();
              }}
              className="w-full bg-blue text-white font-semibold text-sm px-5 py-3 rounded-lg hover:opacity-90 transition cursor-pointer border-none"
            >
              View current progress
            </button>
            <button
              onClick={onClose}
              className="w-full bg-transparent border border-surface-light/30 text-muted-foreground font-medium text-sm px-5 py-3 rounded-lg hover:bg-navy-mid transition cursor-pointer"
            >
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Loading — checking for existing report ──────────────────────
  if (loading || !checked) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-surface border border-surface-light/30 rounded-2xl p-8 max-w-[460px] w-full mx-4 shadow-2xl">
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="w-6 h-6 border-2 border-blue/30 border-t-blue rounded-full animate-spin" />
            <p className="text-muted-foreground text-sm">
              Checking for existing analysis...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Existing report found ───────────────────────────────────────
  if (existing) {
    const ageText =
      existing.ageDays === 0
        ? "today"
        : existing.ageDays === 1
        ? "1 day ago"
        : `${existing.ageDays} days ago`;

    const reportUrl =
      reportType === "fund"
        ? `/funds/${existing.slug}`
        : `/report/${existing.slug}`;

    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-surface border border-surface-light/30 rounded-2xl p-8 max-w-[460px] w-full mx-4 shadow-2xl">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-cgreen/20 flex items-center justify-center">
              <span className="text-2xl">
                {reportType === "fund" ? "📊" : "📈"}
              </span>
            </div>
            <h2 className="text-foreground text-lg font-bold mb-2">
              Report already exists
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed">
              A report for &ldquo;{existing.name}&rdquo; was generated{" "}
              {ageText}.
              {existing.score > 0 && (
                <span className="text-gold font-semibold">
                  {" "}Score: {existing.score.toFixed(1)}/5
                </span>
              )}
            </p>
            {existing.isOutdated && (
              <p className="text-gold text-xs mt-2">
                This report is over 30 days old. Consider generating a fresh one.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                onClose();
                router.push(reportUrl);
              }}
              className="w-full bg-blue text-white font-semibold text-sm px-5 py-3 rounded-lg hover:opacity-90 transition cursor-pointer border-none"
            >
              View existing report &rarr;
            </button>
            <button
              onClick={() => {
                setExisting(null);
              }}
              className="w-full bg-transparent border border-surface-light/30 text-muted-foreground font-medium text-sm px-5 py-3 rounded-lg hover:bg-navy-mid transition cursor-pointer"
            >
              Generate fresh report
            </button>
            <button
              onClick={onClose}
              className="text-dim text-xs text-center hover:text-muted-foreground transition cursor-pointer bg-transparent border-none mt-1"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── No existing report — generation choices ─────────────────────
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-surface border border-surface-light/30 rounded-2xl p-8 max-w-[460px] w-full mx-4 shadow-2xl">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-blue/20 flex items-center justify-center">
            <span className="text-2xl">
              {reportType === "fund" ? "📊" : "📈"}
            </span>
          </div>
          <h2 className="text-foreground text-lg font-bold mb-2">
            Report generation started!
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Your analysis of &ldquo;{reportName}&rdquo; is being generated.
            This typically takes 30-60 seconds.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={() => {
              onClose();
              router.push(generatePageUrl);
            }}
            className="w-full bg-blue text-white font-semibold text-sm px-5 py-3 rounded-lg hover:opacity-90 transition cursor-pointer border-none"
          >
            Watch it generate &rarr;
          </button>
          <button
            onClick={() => {
              onClose();
              startBackgroundGeneration({
                reportType,
                reportName,
                generatePageUrl,
              });
            }}
            className="w-full bg-transparent border border-surface-light/30 text-muted-foreground font-medium text-sm px-5 py-3 rounded-lg hover:bg-navy-mid transition cursor-pointer"
          >
            I&apos;ll read it later
          </button>
        </div>

        <p className="text-dim text-[11px] text-center mt-4">
          We&apos;ll notify you when it&apos;s ready.
        </p>
      </div>
    </div>
  );
}
